/**
 * useGlobalWhatsAppSound — M2.2.6 · Sound engine GLOBALE per l'area Staff.
 *
 * Root cause del bug fix M2.2.5→M2.2.6:
 *   Il vecchio `useNewInboundSound` era montato pagina per pagina.
 *   La Home /staff non lo montava → nessun beep quando un nuovo inbound
 *   arrivava mentre l'operatore era sulla Dashboard Live.
 *
 * Fix definitivo: UN SOLO listener globale montato dentro `StaffShell`
 *   (il layout persistente `/staff/*`). Le singole pagine continuano a
 *   pollare per aggiornare i dati (badge/inbox/thread), ma NON attivano
 *   più il suono → una sola ownership globale.
 *
 * Dedup:
 *   - stato `seenIds` VIVE nel modulo (non nel hook) → invariante fra
 *     cambi route dentro /staff, senza reset di seenIds
 *   - dedup per `last_message_id_meta` (già esposto dall'API M2.2.5)
 *   - primo snapshot dopo mount = init silenzioso (mai beep)
 *   - stesso id → mai secondo beep
 *   - CAP 1000 entries (LRU-order): rimozione ordinata delle più vecchie
 *     per prevenire memory leak in sessioni pluri-giornaliere.
 *
 * Cross-device recovery (Fase A/B):
 *   - Sottoscritto a `lifecycle.on("recovery")` per refetch immediato
 *     dopo sleep/wake, foreground/background, bfcache restore, online.
 *   - Rimossi listener espliciti visibilitychange/focus → delegati a lifecycle.
 *
 * Cross-tab (anti doppio chime):
 *   - BroadcastChannel `sarabi-sound` con leader election tabId-based.
 *   - Solo il tab leader chiama `audioNotifications.play()`.
 *   - Fallback sicuro: se BroadcastChannel non supportato → il tab
 *     corrente resta leader (comportamento identico a pre-esistente).
 *
 * Il singleton `_globalSoundState` è protetto contro doppio mount
 * (React Strict Mode / hot reload): se un secondo `useGlobalWhatsAppSound`
 * si monta mentre il primo è ancora attivo, il secondo diventa no-op.
 */
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { audioNotifications } from "@/lib/audioNotifications";
import { lifecycle } from "@/lib/lifecycle";

const POLL_MS = 12000;
const SEEN_IDS_CAP = 1000;
const HEARTBEAT_MS = 3000;
const PEER_STALE_MS = 8000;

// ---------------------------------------------------------------------------
// Cross-tab leader election (BroadcastChannel, con fallback storage-safe).
// ---------------------------------------------------------------------------
const _tabId = (typeof window !== "undefined"
  && window.crypto && typeof window.crypto.randomUUID === "function")
  ? window.crypto.randomUUID()
  : `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const _peers = new Map();   // tabId → { ts, visible }
let _bc = null;
let _bcHeartbeat = null;

function _bcSupported() {
  return typeof window !== "undefined" && typeof window.BroadcastChannel !== "undefined";
}

function _initBroadcast() {
  if (!_bcSupported() || _bc) return;
  try {
    _bc = new BroadcastChannel("sarabi-sound");
    _bc.onmessage = (e) => {
      const msg = e && e.data;
      if (!msg || msg.tabId === _tabId) return;
      if (msg.type === "hb") {
        _peers.set(msg.tabId, { ts: Date.now(), visible: !!msg.visible });
      } else if (msg.type === "bye") {
        _peers.delete(msg.tabId);
      }
    };
    // Heartbeat periodico
    const beat = () => {
      try {
        _bc.postMessage({
          type: "hb",
          tabId: _tabId,
          visible: typeof document !== "undefined" ? !document.hidden : true,
        });
      } catch { /* silent */ }
      // Prune peers scaduti
      const now = Date.now();
      for (const [id, p] of _peers.entries()) {
        if (now - p.ts > PEER_STALE_MS) _peers.delete(id);
      }
    };
    beat();
    _bcHeartbeat = setInterval(beat, HEARTBEAT_MS);
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", () => {
        try { _bc && _bc.postMessage({ type: "bye", tabId: _tabId }); } catch {}
      });
    }
  } catch { /* fallback: no BC → tab corrente resta unico leader */ }
}

/**
 * Determina se questo tab è "sound leader":
 *   - Priorità ai tab visibili (foreground). Il visibile con tabId lex
 *     minore vince.
 *   - Se nessun tab visibile (tutti hidden), vince il tabId lex minore
 *     complessivo.
 *   - Fallback (no BC / nessun peer conosciuto): questo tab è leader.
 */
function _isSoundLeader() {
  if (!_bc) return true;                       // no BC → sono l'unico
  const iAmVisible = typeof document !== "undefined" ? !document.hidden : true;
  const now = Date.now();
  const alive = [];
  for (const [id, p] of _peers.entries()) {
    if (now - p.ts <= PEER_STALE_MS) alive.push({ id, ...p });
  }
  const visibleAlive = alive.filter((p) => p.visible);
  if (iAmVisible) {
    // vinco se nessun peer visibile ha tabId lex minore
    return visibleAlive.every((p) => _tabId <= p.id);
  }
  if (visibleAlive.length > 0) return false;    // esiste un peer visibile → suona lui
  return alive.every((p) => _tabId <= p.id);    // tra tutti hidden, vince il minore
}

_initBroadcast();

// ---------------------------------------------------------------------------
// Stato dedup singleton
// ---------------------------------------------------------------------------
const _globalSoundState = {
  seenIds: new Set(),
  firstSnapshotDone: false,
  activeOwner: null,        // symbol identifica il mount attivo
  pendingCount: 0,
  debounceTimer: null,
};

function _addSeenWithCap(id) {
  if (_globalSoundState.seenIds.has(id)) return;
  _globalSoundState.seenIds.add(id);
  // Set preserva insertion order in JS → drop del più vecchio
  if (_globalSoundState.seenIds.size > SEEN_IDS_CAP) {
    const oldest = _globalSoundState.seenIds.values().next().value;
    if (oldest !== undefined) _globalSoundState.seenIds.delete(oldest);
  }
}

function _resetForLogout() {
  _globalSoundState.seenIds = new Set();
  _globalSoundState.firstSnapshotDone = false;
  _globalSoundState.activeOwner = null;
  _globalSoundState.pendingCount = 0;
  if (_globalSoundState.debounceTimer) {
    clearTimeout(_globalSoundState.debounceTimer);
    _globalSoundState.debounceTimer = null;
  }
}

// Expose per test e per il logout Staff (window.__sarabiResetGlobalSound()).
if (typeof window !== "undefined") {
  window.__sarabiResetGlobalSound = _resetForLogout;
}

function _triggerBeepDebounced() {
  if (_globalSoundState.debounceTimer) return;
  _globalSoundState.debounceTimer = setTimeout(() => {
    _globalSoundState.debounceTimer = null;
    if (_globalSoundState.pendingCount > 0) {
      _globalSoundState.pendingCount = 0;
      // Cross-tab: suona solo se sono il leader (foreground o tabId minore).
      if (_isSoundLeader()) {
        audioNotifications.play();
      }
    }
  }, 400);
}

function _processSnapshot(items) {
  if (!Array.isArray(items)) return;
  const shaped = items
    .filter((c) => c && c.last_message_id_meta)
    .map((c) => ({
      id_meta: c.last_message_id_meta,
      direction: c.last_direction,
      type: c.last_message_type,
    }));

  if (!_globalSoundState.firstSnapshotDone) {
    for (const m of shaped) _addSeenWithCap(m.id_meta);
    _globalSoundState.firstSnapshotDone = true;
    return;
  }

  let newInbound = 0;
  for (const m of shaped) {
    if (!m.id_meta || _globalSoundState.seenIds.has(m.id_meta)) continue;
    _addSeenWithCap(m.id_meta);
    if (m.direction === "in" && m.type !== "reaction") {
      newInbound += 1;
    }
  }
  if (newInbound > 0) {
    _globalSoundState.pendingCount += newInbound;
    _triggerBeepDebounced();
  }
}

/**
 * Monta il sound engine GLOBALE per Sarabi Staff.
 *
 * Da chiamare UNA SOLA VOLTA a livello di `StaffShell` (layout persistente).
 * Se `enabled=false` (utente non autenticato / niente permesso messaging)
 * l'hook è no-op.
 */
export function useGlobalWhatsAppSound({ enabled = true } = {}) {
  const ownerRef = useRef(null);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    // Ownership: solo un mount attivo alla volta.
    const owner = Symbol("global-sound-owner");
    ownerRef.current = owner;
    if (_globalSoundState.activeOwner === null) {
      _globalSoundState.activeOwner = owner;
    } else {
      // Un altro mount è già owner (hot-reload/strict-mode).
      // Questo mount NON pollera — attende che l'altro si smonti.
      return () => { /* nothing to clean */ };
    }

    let cancelled = false;

    const fetchOnce = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const { data } = await api.get(
          "/staff/messaging/whatsapp/conversations",
          { params: { limit: 100, _t: Date.now() },
            headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } });
        if (cancelled) return;
        _processSnapshot(data?.items || []);
      } catch {
        // silent — il prossimo tick riproverà
      } finally {
        inFlightRef.current = false;
      }
    };

    const schedule = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (cancelled) return;
      // M2.3.5 · NON stoppiamo il polling in background.
      // Il browser stesso throttle (Firefox: min 1s dopo 30s hidden; Chrome:
      // min 1min dopo 5min hidden). Il chime WebAudio funziona anche con
      // tab hidden se AudioContext è già running (sbloccato da gesture prev).
      timerRef.current = setTimeout(async () => {
        await fetchOnce();
        schedule();
      }, POLL_MS);
    };

    // Fetch immediato (silenzioso = primo snapshot init).
    fetchOnce().then(schedule);

    // Recovery cross-device: ritorno da bfcache/sleep/wake/foreground/online.
    const offRecovery = lifecycle.on("recovery", () => {
      if (!cancelled) { fetchOnce(); schedule(); }
    });

    // Cross-tab / other component invalidation → forza refresh (senza reset dedup).
    const onExternal = () => fetchOnce();
    window.addEventListener("sb-messages-changed", onExternal);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      offRecovery();
      window.removeEventListener("sb-messages-changed", onExternal);
      if (_globalSoundState.activeOwner === owner) {
        _globalSoundState.activeOwner = null;
      }
    };
  }, [enabled]);
}

// Export testing helpers (mai chiamati in produzione UI).
export const __soundStateForTest = _globalSoundState;
export const __processSnapshotForTest = _processSnapshot;
export const __tabIdForTest = _tabId;
export const __peersForTest = _peers;
export const __isSoundLeaderForTest = _isSoundLeader;
